import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Descriptions from "/app/src/components/generic/components/descriptions";
import DynamicFields from "/app/src/components/explorer/generic/dynamicFields";
import DateTime from "/app/src/components/generic/formatting/dateTime";
import { orderService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { Order as OrderType } from "/app/src/models";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Box from "/app/src/components/generic/components/box";
import { useHandleNavigationError } from "/app/src/helpers/api";
import { Button, Space } from "antd";
import { toast } from "sonner";
import React from "react";
import HeroModal from "/app/src/components/HeroUi/Modal";
import ModalBuilder from "/app/src/components/HeroUi/helpers/ModalBuilder";
import HeroButton from "/app/src/components/HeroUi/Button";
import { useDisclosure } from "@heroui/react";

/**
 * Order component for displaying details of a single order.
 *
 * @returns {JSX.Element} The rendered order details component.
 */
export default function Order() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const handleNavigationError = useHandleNavigationError();
  const queryClient = useQueryClient();
  const [allocating, setAllocating] = React.useState(false);
  const [deallocating, setDeallocating] = React.useState(false);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  //Get the Single Order from URL
  const { data: order } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => {
      return orderService.getSingle(orderId).catch(() => {
        handleNavigationError();
      });
    },
    initialData: { order: {} },
    retry: false,
    select: (data: { order: OrderType }) => {
      return data.order;
    },
  });

  const allocationStatus = order?.allocationStatus as string | undefined;
  const isNotAllocated =
    allocationStatus === "Not Allocated" || allocationStatus === "Not Allocate";
  const isAllocatedOrPartial =
    allocationStatus === "Fully Allocated" ||
    allocationStatus === "Partially Allocated";
  const allocateLabel = isAllocatedOrPartial
    ? t("translation:reallocate")
    : t("translation:allocate");

  const onAllocate = React.useCallback(async () => {
    if (!orderId) return;
    try {
      setAllocating(true);
      const result = await orderService.allocate(orderId);
      if (result?.fullyAllocated) {
        const hasCount = typeof result?.allocatedLines === "number";
        if (hasCount) {
          toast.success(
            t("translation:order_allocated_success_with_lines", "", {
              count: result.allocatedLines,
            }),
          );
        } else {
          toast.success(t("translation:order_allocated_success"));
        }
      } else {
        const msg =
          result?.message ||
          (result?.status
            ? t("translation:allocation_incomplete_with_status", "", {
                status: result.status,
              })
            : t("translation:order_allocation_unsuccessful"));
        toast.error(msg);
      }
      // refresh order details
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("translation:order_allocation_failed");
      toast.error(msg);
    } finally {
      setAllocating(false);
    }
  }, [orderId, queryClient, t]);

  const onDeallocate = React.useCallback(async () => {
    if (!orderId) return;
    try {
      setDeallocating(true);
      const result = await orderService.deallocate(orderId);
      if (result?.deallocated) {
        toast.success(
          result?.message || t("translation:order_deallocated_success"),
        );
      } else {
        toast.error(
          result?.message || t("translation:order_deallocation_unsuccessful"),
        );
      }
      // refresh order details
      queryClient.invalidateQueries({ queryKey: ["order", orderId] });
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        t("translation:order_deallocation_failed");
      toast.error(msg);
    } finally {
      setDeallocating(false);
    }
  }, [orderId, queryClient, t]);

  const onConfirmDelete = React.useCallback(async () => {
    if (!orderId) return;
    try {
      await orderService.deleteSingle(orderId);
      onOpenChange();
      toast.success(t("translation:order_deleted_success", "Order deleted"));
      // refresh list and navigate back
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      navigate("/explorer/orders");
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.message || t("translation:error");
      toast.error(msg);
    }
  }, [orderId, t, queryClient, navigate, onOpenChange]);

  return (
    <div className="explorer">
      <Helmet>
        <title>{order?.name}</title>
      </Helmet>
      <Box>
        <div className="title">
          <div
            className="noEdit"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h1>{order?.name}</h1>
            <div
              className="titleActions"
              style={{ display: "flex", alignItems: "center" }}
            >
              <Space wrap>
                <Button
                  type="primary"
                  loading={allocating}
                  onClick={onAllocate}
                  disabled={!orderId}
                >
                  {allocateLabel}
                </Button>
                {!isNotAllocated && (
                  <Button
                    danger
                    loading={deallocating}
                    onClick={onDeallocate}
                    disabled={!orderId}
                  >
                    {t("translation:deallocate")}
                  </Button>
                )}
                <Button danger onClick={onOpen} disabled={!orderId}>
                  {t("translation:delete")}
                </Button>
              </Space>
            </div>
          </div>
        </div>
        <h4>{t("translation:information")}</h4>
        <Descriptions
          items={[
            {
              label: t("translation:creation_date"),
              value: <DateTime date={order?.modifiedDate} />,
            },
            {
              label: t("translation:status"),
              value: order?.status,
            },
            {
              label: t("translation:allocation_status", "Allocation Status"),
              value: order?.allocationStatus,
            },
            {
              label: t("translation:owner"),
              value: order?.owner,
            },
            {
              label: t("translation:priority"),
              value: order?.priority,
            },
            {
              label: t("translation:lines_number"),
              value: order?.order_lines?.length,
            },
          ]}
          bordered
        />
        <HeroModal
          disableAnimation={false}
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          placement="top"
        >
          {ModalBuilder({
            warning: true,
            modalHeader: t(
              "translation:confirm_delete_order",
              "Do you want to delete this order?",
            ),
            modalFooter: (
              <>
                <HeroButton
                  size="sm"
                  color="default"
                  variant="bordered"
                  onClick={onOpenChange}
                  className="hover:border-primary-default hover:text-primary-default"
                >
                  {t("translation:do_not_delete")}
                </HeroButton>
                <HeroButton size="sm" color="red" onClick={onConfirmDelete}>
                  {t("translation:delete")}
                </HeroButton>
              </>
            ),
          })}
        </HeroModal>
        <h4>{t("translation:information_fields")}</h4>
        <Descriptions
          items={[
            {
              label: t("translation:info1"),
              value: order?.Info1,
            },
            {
              label: t("translation:info2"),
              value: order?.Info2,
            },
            {
              label: t("translation:info3"),
              value: order?.Info3,
            },
            {
              label: t("translation:info4"),
              value: order?.Info4,
            },
            {
              label: t("translation:info5"),
              value: order?.Info5,
            },
          ]}
          bordered
        />
        <DynamicFields object={order} baseTable="Masterorder" />
      </Box>
    </div>
  );
}
